import { TTL } from "@opensea/utils"
import type {
  Exchange,
  OperationContext as OC,
  Operation,
  OperationResult,
} from "@urql/core"
import { makeOperation } from "@urql/core"
import { map, pipe, tap } from "wonka"

// Reference: https://www.npmjs.com/package/@urql/exchange-request-policy

declare module "@urql/core" {
  interface OperationContext {
    ttl?: number
  }
}

// we want a low default TTL to prevent any stale data issues when navigating between pages
// for cases where staleness is acceptable, a higher TTL can be provided
const DEFAULT_TTL = TTL["2s"]

export const PREFETCH_CONTEXT: Partial<OC> = {
  suspense: false,
}

export const requestPolicyExchange =
  (): Exchange =>
  ({ forward }) => {
    const initialized = Date.now()
    return ops$ => {
      if (typeof window === "undefined") {
        return forward(ops$)
      }

      const operations = new Map()

      const dispatched = new Map<number, number>()
      let counter = 0

      const processIncomingOperation = (operation: Operation): Operation => {
        if (
          operation.kind !== "query" ||
          (operation.context.requestPolicy !== "cache-first" &&
            operation.context.requestPolicy !== "cache-only")
        ) {
          return operation
        }

        const currentTime = Date.now()
        // When an operation passes by we track the current time
        dispatched.set(operation.key, counter)
        queueMicrotask(() => {
          counter = (counter + 1) | 0
        })

        const lastOccurrence = operations.get(operation.key)
        const ttl = operation.context.ttl || DEFAULT_TTL

        // last occurence === undefined could happen for queries that were fetched on server & hydrated
        // If the operation has never been processed before, we don't need to upgrade it
        if (lastOccurrence === undefined) {
          // NOTE: rough heuristic, but if these operations likely executed right after SSR, mark its last occurrence for TTL
          const isLikelyFromHydration = currentTime - initialized < 3000
          if (isLikelyFromHydration) {
            operations.set(operation.key, Date.now())
          }
          return operation
        }

        if (operation.context.requestPolicy !== "cache-only") {
          if (
            lastOccurrence !== undefined &&
            currentTime - lastOccurrence > ttl
          ) {
            return makeOperation(operation.kind, operation, {
              ...operation.context,
              requestPolicy: "cache-and-network",
            })
          }
        }

        return operation
      }

      const processIncomingResults = (result: OperationResult): void => {
        // When we get a result for the operation we check whether it resolved
        // synchronously by checking whether the counter is different from the
        // dispatched counter.
        const lastDispatched = dispatched.get(result.operation.key) || 0

        if (counter !== lastDispatched) {
          // We only delete in the case of a miss to ensure that cache-and-network
          // is properly taken care of
          dispatched.delete(result.operation.key)
          operations.set(result.operation.key, Date.now())
        }
      }

      return pipe(
        forward(pipe(ops$, map(processIncomingOperation))),
        tap(processIncomingResults),
      )
    }
  }
